import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useErrorMessage } from '@/core/i18n';
import { isAbortError } from '@/core/network';
import { parseCoordinates } from './coordinates';
import { resolveQuery } from './resolveQuery';
import type { SearchItem, SearchViewport } from './types';

const TYPING_DEBOUNCE_MS = 250;

interface Options {
  getViewport: () => SearchViewport;
  onSelect: (item: SearchItem) => void;
}

/**
 * Search-as-you-type: debounced queries (immediate for coordinates, which are parsed locally),
 * superseded requests are aborted, keyboard navigation over the suggestions.
 */
export function useSearch({ getViewport, onSelect }: Options) {
  const { t } = useTranslation('search');
  const errorMessage = useErrorMessage();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<SearchItem[]>([]);
  /** The query `items` belong to; null after an error. */
  const [itemsQuery, setItemsQuery] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);
  const abort = useRef<AbortController | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(
    () => () => {
      clearTimeout(timer.current);
      abort.current?.abort();
    },
    [],
  );

  /** Opens the list with `shown` (or `msg` when empty); closes it when both are empty. */
  function showList(shown: SearchItem[], msg: string | null = null) {
    setActive(shown.length ? 0 : -1);
    setMessage(shown.length ? null : msg);
    setOpen(shown.length > 0 || msg !== null);
  }

  async function runSearch(q: string): Promise<SearchItem[] | null> {
    const id = ++seq.current;
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    setLoading(true);
    try {
      const found = await resolveQuery(q, getViewport(), controller.signal);
      if (id !== seq.current) return null;
      setItems(found);
      setItemsQuery(q);
      showList(found, t('nothingFound'));
      return found;
    } catch (err) {
      if (isAbortError(err) || id !== seq.current) return null;
      console.error(err);
      setItems([]);
      setItemsQuery(null);
      showList([], errorMessage(err));
      return null;
    } finally {
      if (id === seq.current) setLoading(false);
    }
  }

  function cancel() {
    clearTimeout(timer.current);
    seq.current++;
    abort.current?.abort();
    setLoading(false);
  }

  function select(item: SearchItem) {
    cancel();
    setQuery(item.name);
    setOpen(false);
    inputRef.current?.blur();
    onSelect(item);
  }

  /** Empties the box and keeps focus in it, ready for a new search. */
  function clear() {
    cancel();
    setQuery('');
    showList([]);
    inputRef.current?.focus();
  }

  function onChange(value: string) {
    setQuery(value);
    clearTimeout(timer.current);
    const q = value.trim();
    if (!q) {
      cancel();
      showList([]);
      return;
    }
    // Coordinates are parsed locally, so no need to wait for the user to stop typing.
    timer.current = setTimeout(() => void runSearch(q), parseCoordinates(q) ? 0 : TYPING_DEBOUNCE_MS);
  }

  async function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    const navigable = open && items.length > 0;
    if (e.key === 'ArrowDown' && navigable) {
      e.preventDefault();
      setActive((active + 1) % items.length);
    } else if (e.key === 'ArrowUp' && navigable) {
      e.preventDefault();
      setActive((active - 1 + items.length) % items.length);
    } else if (e.key === 'Escape') {
      cancel();
      setOpen(false);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const q = query.trim();
      if (!q) return;
      if (itemsQuery === q && items.length) {
        select(items[Math.max(0, active)]);
      } else {
        clearTimeout(timer.current);
        const found = await runSearch(q);
        if (found?.length) select(found[0]);
      }
    }
  }

  function onFocus() {
    if (itemsQuery === query.trim() && items.length) showList(items);
  }

  return {
    inputProps: {
      ref: inputRef,
      value: query,
      onChange: (e: { target: { value: string } }) => onChange(e.target.value),
      onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void onKeyDown(e),
      onFocus,
      onBlur: () => setOpen(false),
    },
    loading,
    list: { open, items, message, active },
    highlight: setActive,
    select,
    clear,
  };
}
